import { createContext, ReactNode, useCallback, useContext, useEffect, useRef } from 'react';

import mixpanel from 'mixpanel-browser';
import { useLocation } from 'react-router';

import { incrementDatasetEngagement } from 'utilities/consent/cookies';
import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';

export type SessionCounterKey =
  | 'copy_dataset_value_count'
  | 'export_dataset_count'
  | 'exchange_clicked_count'
  | 'view_related_dataset_count'
  | 'impact_assessment_action_count'
  | 'history_tab_action_count'
  | 'lci_results_tab_action_count';

type DatasetSessionContextType = {
  increment: (key: SessionCounterKey) => void;
};

const DatasetSessionContext = createContext<DatasetSessionContextType | null>(null);

export const useDatasetSession = () => {
  const ctx = useContext(DatasetSessionContext);
  if (!ctx) return { increment: () => {} };
  return ctx;
};

const INITIAL_COUNTERS: Record<SessionCounterKey, number> = {
  copy_dataset_value_count: 0,
  export_dataset_count: 0,
  exchange_clicked_count: 0,
  view_related_dataset_count: 0,
  impact_assessment_action_count: 0,
  history_tab_action_count: 0,
  lci_results_tab_action_count: 0,
};

const ENGAGEMENT_THRESHOLD = 120;
const HIDDEN_FLUSH_DELAY = 5 * 60 * 1000;

const isEngaged = (counters: Record<SessionCounterKey, number>, tabTimes: Map<string, number>): boolean => {
  if (counters.copy_dataset_value_count > 0) return true;
  if (counters.export_dataset_count > 0) return true;
  if (counters.exchange_clicked_count >= 2) return true;
  if (counters.view_related_dataset_count > 0) return true;
  if (counters.impact_assessment_action_count > 2) return true;
  if (counters.history_tab_action_count > 2) return true;
  if (counters.lci_results_tab_action_count > 2) return true;

  for (const seconds of tabTimes.values()) {
    if (seconds >= ENGAGEMENT_THRESHOLD) return true;
  }

  return false;
};

const parseDatasetUrl = (pathname: string) => {
  const parts = pathname.split('/');
  const datasetIndex = parts.indexOf('dataset');
  if (datasetIndex === -1) return { spoldId: null, tab: null };
  return {
    spoldId: parts[datasetIndex + 1] || null,
    tab: parts[datasetIndex + 2] || 'documentation',
  };
};

export const DatasetSessionProvider = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const { spoldId, tab } = parseDatasetUrl(pathname);

  const countersRef = useRef<Record<SessionCounterKey, number>>({ ...INITIAL_COUNTERS });
  const tabTimesRef = useRef<Map<string, number>>(new Map());
  const activeTabRef = useRef<string | null>(tab);
  const tabEntryTimeRef = useRef(Date.now());
  const sessionStartRef = useRef(Date.now());
  const flushedRef = useRef(false);
  const spoldIdRef = useRef(spoldId);
  const hiddenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const increment = useCallback((key: SessionCounterKey) => {
    if (!spoldIdRef.current) return;
    countersRef.current[key]++;
  }, []);

  const accumulateTabTime = useCallback(() => {
    const now = Date.now();
    const elapsed = (now - tabEntryTimeRef.current) / 1000;
    const currentTab = activeTabRef.current;
    if (currentTab) {
      tabTimesRef.current.set(currentTab, (tabTimesRef.current.get(currentTab) || 0) + elapsed);
    }
    tabEntryTimeRef.current = now;
  }, []);

  const resetSession = useCallback(() => {
    if (hiddenTimerRef.current) {
      clearTimeout(hiddenTimerRef.current);
      hiddenTimerRef.current = null;
    }
    countersRef.current = { ...INITIAL_COUNTERS };
    tabTimesRef.current = new Map();
    tabEntryTimeRef.current = Date.now();
    sessionStartRef.current = Date.now();
    flushedRef.current = false;
  }, []);

  const flushSession = useCallback(
    (useBeacon: boolean) => {
      if (flushedRef.current) return;
      if (!spoldIdRef.current) return;

      accumulateTabTime();

      const counters = countersRef.current;
      const tabTimes = Object.fromEntries(tabTimesRef.current);
      const maxTabTime = Math.max(0, ...tabTimesRef.current.values());
      const tabsVisited = [...tabTimesRef.current.keys()];
      const sessionDuration = (Date.now() - sessionStartRef.current) / 1000;

      const hasInteractions = Object.values(counters).some((count) => count > 0);
      if (sessionDuration < 5 && !hasInteractions) return;

      flushedRef.current = true;

      const properties = {
        spold_id: spoldIdRef.current,
        session_duration_seconds: Math.round(sessionDuration),
        is_engaged: isEngaged(counters, tabTimesRef.current),
        ...counters,
        tab_times: tabTimes,
        max_tab_time_seconds: Math.round(maxTabTime),
        tabs_visited: tabsVisited,
      };

      if (properties.is_engaged) {
        incrementDatasetEngagement();
      }

      if (useBeacon) {
        mixpanel.track(MIXPANEL_EVENTS.DATASET_PAGE_SESSION, properties, { transport: 'sendBeacon' });
      } else {
        mixpanel.track(MIXPANEL_EVENTS.DATASET_PAGE_SESSION, properties);
      }
    },
    [accumulateTabTime]
  );

  useEffect(() => {
    if (!spoldId || tab === activeTabRef.current) return;
    accumulateTabTime();
    activeTabRef.current = tab;
  }, [tab, spoldId, accumulateTabTime]);

  useEffect(() => {
    const prevSpoldId = spoldIdRef.current;

    if (prevSpoldId && prevSpoldId !== spoldId) {
      flushSession(false);
      resetSession();
    } else if (!prevSpoldId && spoldId) {
      resetSession();
    }

    spoldIdRef.current = spoldId;
    activeTabRef.current = tab;
  }, [spoldId, tab, flushSession, resetSession]);

  useEffect(() => {
    return () => {
      flushSession(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        accumulateTabTime();

        hiddenTimerRef.current = setTimeout(() => {
          flushSession(true);
        }, HIDDEN_FLUSH_DELAY);
      } else if (document.visibilityState === 'visible') {
        if (hiddenTimerRef.current) {
          clearTimeout(hiddenTimerRef.current);
          hiddenTimerRef.current = null;
        }

        if (!flushedRef.current) {
          tabEntryTimeRef.current = Date.now();
        }
      }
    };

    const handlePageHide = () => {
      if (hiddenTimerRef.current) {
        clearTimeout(hiddenTimerRef.current);
        hiddenTimerRef.current = null;
      }
      flushSession(true);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      if (hiddenTimerRef.current) {
        clearTimeout(hiddenTimerRef.current);
      }
    };
  }, [flushSession, accumulateTabTime]);

  return <DatasetSessionContext.Provider value={{ increment }}>{children}</DatasetSessionContext.Provider>;
};
